import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType, useOutlet } from 'react-router-dom';
import { AnimatePresence, LazyMotion, MotionConfig, domAnimation, m } from 'motion/react';

const EASE: [number, number, number, number] = [0.2, 0.7, 0.2, 1];

const SCROLL_STORE = 'hvdm:scroll-positions';

const readPositions = (): Record<string, number> => {
  try {
    return JSON.parse(sessionStorage.getItem(SCROLL_STORE) ?? '{}');
  } catch {
    return {};
  }
};

const FrozenOutlet = () => {
  const outlet = useOutlet();
  const [frozen] = useState(outlet);
  return frozen;
};

/* Cross-fades routes on navigation. Replaces react-router's <ScrollRestoration />:
   that component scrolls the window the instant the location changes, which
   visibly yanks the outgoing page mid-exit. Instead we scroll between the exit
   and enter animations (restoring the saved offset on back/forward, top otherwise). */
const PageTransition = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  const pathname = location.pathname.replace(/\/+$/, '') || '/';

  const positions = useRef<Record<string, number>>(readPositions());
  const prevKey = useRef(location.key);

  // Snapshot the outgoing page's offset the moment the location changes — the
  // window hasn't been scrolled yet, so it still reflects the old page.
  useLayoutEffect(() => {
    if (prevKey.current !== location.key) {
      positions.current[prevKey.current] = window.scrollY;
      prevKey.current = location.key;
    }
  }, [location.key]);

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    const persist = () => {
      positions.current[prevKey.current] = window.scrollY;
      sessionStorage.setItem(SCROLL_STORE, JSON.stringify(positions.current));
    };
    window.addEventListener('pagehide', persist);
    return () => window.removeEventListener('pagehide', persist);
  }, []);

  // Restore the offset after a full page reload.
  useEffect(() => {
    const saved = positions.current[location.key];
    if (saved != null) window.scrollTo(0, saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollForNewPage = () => {
    const saved = navigationType === 'POP' ? positions.current[location.key] : undefined;
    window.scrollTo({ top: saved ?? 0, left: 0, behavior: 'instant' });
  };

  return (
    <LazyMotion features={domAnimation} strict>
      <MotionConfig reducedMotion="user">
        <AnimatePresence mode="wait" initial={false} onExitComplete={scrollForNewPage}>
          <m.div
            key={pathname}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE } }}
            exit={{ opacity: 0, y: -12, transition: { duration: 0.22, ease: 'easeIn' } }}>
            <FrozenOutlet />
          </m.div>
        </AnimatePresence>
      </MotionConfig>
    </LazyMotion>
  );
};

export default PageTransition;
